import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { Edit3,ArrowLeft,Share2   } from 'lucide-react';
import Swal from 'sweetalert2';

// Context & API
import { useAuth } from '../../../context/AuthContext';
import { fetchUserJogetProfileApi, fetchUserJogetProfileUUDIApi } from '../../../services/api';
import MetaTags from '../../../components/MetaTags';

// Helpers & Sub-Components
import { calculateAge, getSkillIcon, calculateAIScores } from '../../../components/candidateProfile/ProfileHelpers';
import ProfileSidebar from '../../../components/candidateProfile/ProfileSidebar';
import ProfilePortfolio from '../../../components/candidateProfile/ProfilePortfolio';
import ProfileAnalytics from '../../../components/candidateProfile/ProfileAnalytics';
import './CandidateProfile.css';

export default function CandidateProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const profileId = params.id;
  const hasAdminToken = !!localStorage.getItem('adminToken');
  const userRole = localStorage.getItem('role') || localStorage.getItem('userType') || '';
  const isCastingDirector = userRole.toUpperCase().includes('CASTING') || userRole.toUpperCase().includes('CD');

  const isQueryViewMode = query.get("knowMore") === "true" || query.get("viewOnly") === "true" || query.get("mode") === "view" || query.get("isCD") === "true";
  
  // 🔒 READ ONLY CHECK: Hide Edit buttons if viewing someone else's profile ID or viewer is CD/Admin
  const isReadOnly = !!profileId || isCastingDirector || isQueryViewMode || hasAdminToken;

  const [profileData, setProfileData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fixed Data Loading Logic
  const loadProfileData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let response = null;

      // Case 1: If profileId is passed in URL (Viewing Candidate Profile)
      if (profileId && profileId !== "add") {
        try {
          // First try UUID API
          response = await fetchUserJogetProfileUUDIApi(profileId);
        } catch (e) {
          response = null;
        }

        // Fallback to Standard Profile API with profileId if UUID fetch returns empty
        if (!response?.data?.jogetData && !response?.data) {
          try {
            response = await fetchUserJogetProfileApi(profileId);
          } catch (e) {
            response = null;
          }
        }
      } 
      // Case 2: Viewing Own Profile (No profileId in URL)
      else if (user?.id) {
        response = await fetchUserJogetProfileApi(user.id);
      } else {
        response = await fetchUserJogetProfileApi();
      }

      if (response && (response.data?.jogetData || response.data)) {
        const jogetRes = response.data.jogetData || response.data;
        setProfileData(jogetRes);
        setProfileImage(response.data.profileImageUrl || jogetRes?.profileImage || null);
      } else {
        setError("Candidate profile data not found.");
      }
    } catch (err) {
      console.error("Error fetching dynamic profile:", err);
      setError("Failed to load candidate profile details from server.");
    } finally {
      setLoading(false);
    }
  }, [user, profileId]);

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

   const handleEditProfile = () => {
    if (isReadOnly) return;
    if (profileId) {
      navigate(`/dashboard/profile/edit/${profileId}`);
    } else {
      navigate('/dashboard/profile/edit');
    }
  };

  // ✅ Share profile link
  const handleShareProfile = async () => {
    const shareTitle = `${tp.fullName || 'Candidate'} - Profile`;
    const shareText = `Check out the candidate profile for ${tp.fullName || 'Candidate'} on BookMyAudition!`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Share canceled or failed', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        Swal.fire({
          icon: 'success',
          title: 'Link Copied!',
          text: 'Profile link copied to clipboard.',
          width: '380px',
          background: '#131824',
          color: '#ffffff',
          confirmButtonColor: '#dc3545',
          customClass: {
            title: 'text-white fw-bold fs-5 mb-2',
            htmlContainer: 'text-muted mb-3 fs-6',
            confirmButton: 'btn btn-danger px-4 py-2 fw-bold'
          },
          buttonsStyling: false
        });
      } catch (err) {
        console.error('Failed to copy link', err);
      }
    }
  };
  

  const navigateToEditStep = (stepIndex) => {
    if (isReadOnly) return;
    if (profileId) {
      navigate(`/dashboard/profile/edit/${profileId}?step=${stepIndex}`);
    } else {
      navigate(`/dashboard/profile/edit?step=${stepIndex}`);
    }
  };

  if (loading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;
  if (error) return <Alert variant="danger" className="m-3">{error}</Alert>;

  const tp = profileData || {};
  const talentAge = calculateAge(tp.dateOfBirth);

  const languagesList = tp.languages ? tp.languages.split(',').map(l => ({ l: l.trim(), s: 'Fluent', r: 'Fluent', w: 'Fluent' })) : [
    { l: tp.nationality || 'English', s: 'Fluent', r: 'Fluent', w: 'Fluent' }
  ];

  const skillsList = tp.talent ? tp.talent.split(',').map(s => ({ 
      name: s.trim(), 
      icon: getSkillIcon(s) 
  })) : [];

  const mediaGallery = Array.isArray(tp.mediaGallery) ? tp.mediaGallery : [];
  const portfolioPhotos = mediaGallery.filter(item => item.mediaType === 'image').map(item => item.mediaUrl);
  const portfolioVideos = mediaGallery.filter(item => item.mediaType === 'video').map(item => item.mediaUrl);

  const ai = calculateAIScores(tp);

  const performanceMetrics = [
    { i: '😊', l: 'Emotion Expression', s: ai.emotion },
    { i: '🎙️', l: 'Voice Clarity', s: ai.voice },
    { i: '💬', l: 'Dialogue Delivery', s: ai.dialogue },
    { i: '😎', l: 'Confidence', s: ai.confidence },
    { i: '🧍', l: 'Body Language', s: ai.body },
    { i: '📹', l: 'Camera Presence', s: ai.camera },
    { i: '⏱️', l: 'Pace & Timing', s: ai.pace },
    { i: '🎭', l: 'Versatility', s: ai.versatility }
  ];

  const experiences = Array.isArray(tp.experiences) ? tp.experiences : [];
  
  const filmsCount = experiences.filter(exp => {
      const type = (exp.projectType?.label || exp.projectType || '').toLowerCase();
      return type.includes('film') || type.includes('movie');
  }).length;

  const webSeriesCount = experiences.filter(exp => {
      const type = (exp.projectType?.label || exp.projectType || '').toLowerCase();
      return type.includes('web') || type.includes('series');
  }).length;

  const tvShowsCount = experiences.filter(exp => {
      const type = (exp.projectType?.label || exp.projectType || '').toLowerCase();
      return type.includes('tv') || type.includes('serial') || type.includes('show');
  }).length;

  const adsCount = experiences.filter(exp => {
      const type = (exp.projectType?.label || exp.projectType || '').toLowerCase();
      return type.includes('ad') || type.includes('tvc') || type.includes('commercial');
  }).length;

  const theatreCount = experiences.filter(exp => {
      const type = (exp.projectType?.label || exp.projectType || '').toLowerCase();
      return type.includes('theatre') || type.includes('play');
  }).length;

  const totalProjectsCount = experiences.length;
  const estimatedYears = totalProjectsCount > 0 ? (totalProjectsCount * 0.4 + 0.2).toFixed(1) : "0.0";

  const educations = Array.isArray(tp.educations) ? tp.educations : [];

  return (
    <div className="container-fluid py-4 px-xl-5 profile-main-container text-white">
      <MetaTags title={`${tp.fullName || 'Candidate'} - Profile`} />

      {/* HEADER SECTION */}
      <div className="d-flex justify-content-between align-items-center top-header cp-top-header pb-3 mb-4">
        <div className="d-flex align-items-center gap-4">
          {/* <div className="fw-bold fs-5 text-white">
            BOOKMY<br/><span className="text-danger">AUDITION</span>
          </div> */}
          {isReadOnly && (
            <button 
              className="btn btn-outline-secondary text-white d-flex align-items-center gap-2" 
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={18} /> Back
            </button>
          )}
          <div className="border-start border-secondary ps-4">
            <h4 className="fw-bold mb-1">CANDIDATE PROFILE</h4>
            <div className="text-muted cp-header-subtitle">Complete profile helps in better role matching</div>
          </div>
        </div>

        {/* 🔒 HIDE Edit Button for Casting Director or in Read-Only mode */}
         <div className="d-flex gap-2">
          {/* ✅ Always visible Share Profile button */}
          <button 
            className="btn btn-outline-danger text-white btn-sm fw-bold d-flex align-items-center gap-1 px-3" 
            onClick={handleShareProfile}
          >
            <Share2 size={16}/> Share Profile
          </button>
          {/* 🔒 HIDE Edit Button for Casting Director or in Read-Only mode */}
          {!isReadOnly && (
            <button className="btn btn-outline-danger text-white btn-sm fw-bold d-flex align-items-center gap-1" onClick={handleEditProfile}>
              <Edit3 size={16}/> Edit Profile
            </button>
          )}
        </div>
      </div>

      <div className="row g-4">
        {/* Sidebar Component */}
        <ProfileSidebar 
          tp={tp} 
          profileImage={profileImage} 
          talentAge={talentAge} 
          handleEditProfile={isReadOnly ? null : handleEditProfile} 
          isReadOnly={isReadOnly}
        />

        {/* Portfolio Component */}
        <ProfilePortfolio 
          tp={tp}
          languagesList={languagesList}
          skillsList={skillsList}
          educations={educations}
          portfolioPhotos={portfolioPhotos}
          portfolioVideos={portfolioVideos}
          filmsCount={filmsCount}
          webSeriesCount={webSeriesCount}
          tvShowsCount={tvShowsCount}
          adsCount={adsCount}
          theatreCount={theatreCount}
          estimatedYears={estimatedYears}
          navigateToEditStep={isReadOnly ? null : navigateToEditStep}
          isReadOnly={isReadOnly}
        />

        {/* Analytics Component */}
        <ProfileAnalytics 
          ai={ai}
          performanceMetrics={performanceMetrics}
          tp={tp}
        />
      </div>
    </div>
  );
}